import { useCallback, useEffect, useRef } from "react";
import { hashKey, type QueryKey } from "@tanstack/react-query";

type PreviousQueryRef = { readonly queryHash: string } | undefined;

/**
 * Placeholder policy for selection-driven queries: while the next key loads,
 * keep the previous key's result on screen (the consumer dims it via
 * `isPlaceholderData`) instead of unmounting into a skeleton.
 *
 * This differs from TanStack's `keepPreviousData` in one way. That helper
 * reaches back to the last query that ever had data, so after a disabled gap
 * (no thread selected, a root workflow with no parent) it would resurface an
 * unrelated record. This policy keeps data only from the key rendered in the
 * immediately preceding render, and only while `enabled` is true.
 *
 * Spread the result into the `useQuery` options.
 */
export function useKeepAdjacentData(queryKey: QueryKey, enabled: boolean) {
    const currentHash = hashKey(queryKey);
    const previousHash = useRef(currentHash);
    useEffect(() => {
        previousHash.current = currentHash;
    }, [currentHash]);

    // Stable identity: TanStack holds the current placeholder across
    // re-renders only while the `placeholderData` function is the same.
    const placeholderData = useCallback(
        <T>(previousData: T | undefined, previousQuery: PreviousQueryRef): T | undefined =>
            previousQuery !== undefined && previousQuery.queryHash === previousHash.current
                ? previousData
                : undefined,
        [],
    );

    return enabled ? { placeholderData } : {};
}
